/**
 * Timeline Renderer — SVG-based vertical timeline
 * Plots concepts with date_value on a time axis (top = older, bottom = newer),
 * with crosslink arcs between them.
 */

import { getNodeColorFromTags } from '../lib/colorUtils.js';

const NS = 'http://www.w3.org/2000/svg';

interface TimelineColors {
  axis: string;
  tick: string;
  tickLabel: string;
  nodeFill: string;
  nodeStroke: string;
  nodeHover: string;
  text: string;
  textDim: string;
  arc: string;
  arcHover: string;
  signal: string;
}

const COLORS: TimelineColors = {
  axis: '#5c584e',
  tick: '#5c584e',
  tickLabel: '#5c584e',
  nodeFill: '#1a1917',
  nodeStroke: '#5c584e',
  nodeHover: '#111110',
  text: '#e8e4db',
  textDim: '#5c584e',
  arc: '#5c584e',
  arcHover: '#e8e4db',
  signal: '#FF4F1A',
};

interface TimelineNode {
  slug: string;
  title?: string;
  date_value?: string | number | null;
  tags?: { color: string; name?: string; id?: number }[];
}

interface TimelineEdge {
  source: string;
  target: string;
  label?: string;
  relation_type_id?: number | null;
}

interface TimelineData {
  nodes: TimelineNode[];
  edges: TimelineEdge[];
}

interface TimelineRendererOptions {
  onNodeClick?: (slug: string) => void;
  onEdgeClick?: (info: { edgeData: TimelineEdge; sourceSlug: string; targetSlug: string }) => void;
}

interface TimelineItem extends TimelineNode {
  year: number;
}

interface XOffset {
  x: number;
  right: boolean;
  tier: number;
}

interface TickIntervals {
  major: number;
  minor: number;
}

export interface TimelineRendererInstance {
  setVisibleTypes(typeIds: number[] | null): void;
  destroy(): void;
}

/** Convert compact date_value to a numeric year (float) for positioning */
function dateToYear(val: string | number | null | undefined): number | null {
  if (!val) return null;
  const str = String(val).trim();
  if (!str) return null;

  const bc = str.startsWith('-');
  const digits = bc ? str.slice(1) : str;

  let year: number;
  if (digits.length === 4) {
    year = parseInt(digits, 10);
  } else if (digits.length === 6) {
    year = parseInt(digits.slice(0, 4), 10);
    const month = parseInt(digits.slice(4, 6), 10);
    year += (month - 1) / 12;
  } else if (digits.length === 8) {
    year = parseInt(digits.slice(0, 4), 10);
    const month = parseInt(digits.slice(4, 6), 10);
    const day = parseInt(digits.slice(6, 8), 10);
    year += (month - 1) / 12 + (day - 1) / 365;
  } else {
    return null;
  }

  return bc ? -year : year;
}

/** Format a numeric year for display on axis ticks */
function formatYear(year: number): string {
  if (year < 0) return `${Math.abs(Math.round(year))} BC`;
  return String(Math.round(year));
}

/**
 * Choose tick interval based on years visible per pixel.
 * Returns { major, minor } intervals in years.
 */
function chooseTicks(yearsPerPixel: number, containerLength: number): TickIntervals {
  const yearsVisible = yearsPerPixel * containerLength;

  // Target roughly 8-15 major ticks on screen
  const intervals = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
  let major = 10;
  for (const iv of intervals) {
    if (yearsVisible / iv <= 20) {
      major = iv;
      break;
    }
  }

  let minor = major / 5;
  if (minor < 1) minor = 1;

  return { major, minor };
}

export function createTimelineRenderer(
  container: HTMLElement,
  data: TimelineData,
  options: TimelineRendererOptions = {}
): TimelineRendererInstance {
  const { onNodeClick = () => {}, onEdgeClick = () => {} } = options;

  if (!data.nodes.length) return { destroy() {}, setVisibleTypes() {} };

  // --- Parse dates and position nodes ---
  const items: TimelineItem[] = [];
  for (const node of data.nodes) {
    const year = dateToYear(node.date_value);
    if (year === null) continue;
    items.push({ ...node, year });
  }

  if (items.length === 0) return { destroy() {}, setVisibleTypes() {} };

  // Sort by year
  items.sort((a, b) => a.year - b.year);

  // Build slug -> year lookup
  const slugToYear = new Map<string, number>();
  const slugToNode = new Map<string, TimelineItem>();
  for (const item of items) {
    slugToYear.set(item.slug, item.year);
    slugToNode.set(item.slug, item);
  }

  // --- Stagger X positions to reduce label overlap ---
  // Group items that are very close together, alternate left/right of axis.
  const NODE_R = 6;
  const AXIS_X = 0; // axis at x=0 in SVG coords

  // Assign x offsets: alternate right and left of axis
  const xOffsets = new Map<string, XOffset>();
  for (let i = 0; i < items.length; i++) {
    const right = i % 2 === 0;
    const tier = Math.floor(i / 2) % 3; // stagger in tiers
    // Check for overlapping dates — increase tier
    let adjustedTier = tier;
    for (let j = 0; j < i; j++) {
      const prevItem = items[j];
      const yearDiff = Math.abs(items[i].year - prevItem.year);
      // If very close and same side, bump tier
      if (yearDiff < 0.5 && (i % 2 === j % 2)) {
        adjustedTier = Math.max(adjustedTier, (xOffsets.get(prevItem.slug)!.tier || 0) + 1);
      }
    }
    const baseOffset = 80 + adjustedTier * 110;
    xOffsets.set(items[i].slug, {
      x: right ? AXIS_X + baseOffset : AXIS_X - baseOffset,
      right,
      tier: adjustedTier,
    });
  }

  // --- Determine initial view bounds ---
  const minYear = items[0].year;
  const maxYear = items[items.length - 1].year;
  const yearSpan = Math.max(maxYear - minYear, 1);

  // Create SVG
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('class', 'timeline-svg');
  svg.style.width = '100%';
  svg.style.height = '100%';
  container.appendChild(svg);

  // Get actual pixel dimensions
  const rect = container.getBoundingClientRect();
  const W = rect.width || 800;
  const H = rect.height || 400;

  // State
  let yearsPerPixel = (yearSpan * 1.3) / H; // start with some padding (vertical span)
  let centerYear = (minYear + maxYear) / 2;
  let visibleTypes: Set<number | null> | null = null; // null = show all
  let activeArcGroup: SVGGElement | null = null;

  // --- Drawing functions ---
  function yearToY(year: number): number {
    return (year - centerYear) / yearsPerPixel;
  }

  function yToYear(y: number): number {
    return centerYear + y * yearsPerPixel;
  }

  function draw(): void {
    // Clear SVG
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const cRect = container.getBoundingClientRect();
    const w = cRect.width || W;
    const h = cRect.height || H;

    // SVG viewBox centered at (0, 0)
    svg.setAttribute('viewBox', `${-w / 2} ${-h / 2} ${w} ${h}`);

    // --- Draw axis (vertical line) ---
    const axisGroup = document.createElementNS(NS, 'g') as SVGGElement;
    axisGroup.setAttribute('class', 'timeline-axis');

    const axisLine = document.createElementNS(NS, 'line') as SVGLineElement;
    axisLine.setAttribute('x1', String(AXIS_X));
    axisLine.setAttribute('x2', String(AXIS_X));
    axisLine.setAttribute('y1', String(-h / 2));
    axisLine.setAttribute('y2', String(h / 2));
    axisLine.setAttribute('stroke', COLORS.axis);
    axisLine.setAttribute('stroke-width', '1');
    axisGroup.appendChild(axisLine);

    // Ticks
    const { major, minor } = chooseTicks(yearsPerPixel, h);
    const viewMinYear = yToYear(-h / 2);
    const viewMaxYear = yToYear(h / 2);
    const tickStart = Math.floor(viewMinYear / minor) * minor;

    for (let yr = tickStart; yr <= viewMaxYear; yr += minor) {
      const y = yearToY(yr);
      if (y < -h / 2 - 10 || y > h / 2 + 10) continue;

      const isMajor = Math.abs(yr % major) < 0.001;
      const tickLen = isMajor ? 10 : 5;

      const tick = document.createElementNS(NS, 'line') as SVGLineElement;
      tick.setAttribute('x1', String(AXIS_X - tickLen / 2));
      tick.setAttribute('x2', String(AXIS_X + tickLen / 2));
      tick.setAttribute('y1', String(y));
      tick.setAttribute('y2', String(y));
      tick.setAttribute('stroke', COLORS.tick);
      tick.setAttribute('stroke-width', isMajor ? '1' : '0.5');
      axisGroup.appendChild(tick);

      if (isMajor) {
        const label = document.createElementNS(NS, 'text') as SVGTextElement;
        label.setAttribute('x', String(AXIS_X + 14));
        label.setAttribute('y', String(y));
        label.setAttribute('text-anchor', 'start');
        label.setAttribute('dominant-baseline', 'middle');
        label.setAttribute('fill', COLORS.tickLabel);
        label.setAttribute('font-family', "'JetBrains Mono', monospace");
        label.setAttribute('font-size', '11');
        label.textContent = formatYear(yr);
        axisGroup.appendChild(label);
      }
    }
    svg.appendChild(axisGroup);

    // --- Draw arcs (edges between concepts) ---
    // Arcs curve to the left of the axis.
    const arcGroup = document.createElementNS(NS, 'g') as SVGGElement;
    arcGroup.setAttribute('class', 'timeline-arcs');

    for (const edge of data.edges) {
      const srcYear = slugToYear.get(edge.source);
      const tgtYear = slugToYear.get(edge.target);
      if (srcYear === undefined || tgtYear === undefined) continue;

      // Filter by visible types
      if (visibleTypes !== null) {
        const typeId = edge.relation_type_id || null;
        if (!visibleTypes.has(typeId)) continue;
      }

      const y1 = yearToY(srcYear);
      const y2 = yearToY(tgtYear);

      // Arc width proportional to span, with a minimum
      const span = Math.abs(y2 - y1);
      const arcWidth = Math.max(span * 0.3, 40);

      // Draw arc to the left of the axis
      const midY = (y1 + y2) / 2;
      const arcX = AXIS_X - arcWidth - 10;
      const d = `M ${AXIS_X} ${y1} Q ${arcX} ${midY}, ${AXIS_X} ${y2}`;

      const g = document.createElementNS(NS, 'g') as SVGGElement;
      g.setAttribute('class', 'timeline-arc');
      g.style.cursor = 'pointer';

      // Hit area
      const hitArea = document.createElementNS(NS, 'path') as SVGPathElement;
      hitArea.setAttribute('d', d);
      hitArea.setAttribute('fill', 'none');
      hitArea.setAttribute('stroke', 'transparent');
      hitArea.setAttribute('stroke-width', '12');
      g.appendChild(hitArea);

      // Visible path
      const path = document.createElementNS(NS, 'path') as SVGPathElement;
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', COLORS.arc);
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('stroke-opacity', '0.6');
      g.appendChild(path);

      // Label on arc
      if (edge.label) {
        const labelText = document.createElementNS(NS, 'text') as SVGTextElement;
        labelText.setAttribute('x', String(arcX + arcWidth * 0.3));
        labelText.setAttribute('y', String(midY));
        labelText.setAttribute('text-anchor', 'middle');
        labelText.setAttribute('dominant-baseline', 'middle');
        labelText.setAttribute('fill', COLORS.textDim);
        labelText.setAttribute('font-family', "'JetBrains Mono', monospace");
        labelText.setAttribute('font-size', '10');
        labelText.setAttribute('pointer-events', 'none');
        labelText.textContent = edge.label;
        g.appendChild(labelText);
      }

      g.addEventListener('mouseenter', () => {
        path.setAttribute('stroke', COLORS.arcHover);
        path.setAttribute('stroke-width', '2.5');
        path.setAttribute('stroke-opacity', '1');
      });
      g.addEventListener('mouseleave', () => {
        if (activeArcGroup !== g) {
          path.setAttribute('stroke', COLORS.arc);
          path.setAttribute('stroke-width', '1.5');
          path.setAttribute('stroke-opacity', '0.6');
        }
      });
      g.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
        // Clear previous highlight
        if (activeArcGroup) {
          const prevPath = activeArcGroup.querySelector('path:nth-child(2)') as SVGPathElement | null;
          if (prevPath) {
            prevPath.setAttribute('stroke', COLORS.arc);
            prevPath.setAttribute('stroke-width', '1.5');
            prevPath.setAttribute('stroke-opacity', '0.6');
          }
        }
        path.setAttribute('stroke', COLORS.arcHover);
        path.setAttribute('stroke-width', '2.5');
        path.setAttribute('stroke-opacity', '1');
        activeArcGroup = g;
        onEdgeClick({ edgeData: edge, sourceSlug: edge.source, targetSlug: edge.target });
      });

      arcGroup.appendChild(g);
    }
    svg.appendChild(arcGroup);

    // --- Draw nodes ---
    const nodeGroup = document.createElementNS(NS, 'g') as SVGGElement;
    nodeGroup.setAttribute('class', 'timeline-nodes');

    for (const item of items) {
      const y = yearToY(item.year);
      // Skip if off-screen (with margin)
      if (y < -h / 2 - 100 || y > h / 2 + 100) continue;

      const offset = xOffsets.get(item.slug);
      const x = offset ? offset.x : 80;
      const borderColor = getNodeColorFromTags(item.tags, 'hex') as string;

      const g = document.createElementNS(NS, 'g') as SVGGElement;
      g.setAttribute('class', 'timeline-node');
      g.setAttribute('data-slug', item.slug);
      g.style.cursor = 'pointer';

      // Stem line from axis to node
      const stem = document.createElementNS(NS, 'line') as SVGLineElement;
      stem.setAttribute('x1', String(AXIS_X));
      stem.setAttribute('x2', String(x));
      stem.setAttribute('y1', String(y));
      stem.setAttribute('y2', String(y));
      stem.setAttribute('stroke', COLORS.axis);
      stem.setAttribute('stroke-width', '1');
      stem.setAttribute('stroke-opacity', '0.4');
      g.appendChild(stem);

      // Node circle
      const circle = document.createElementNS(NS, 'circle') as SVGCircleElement;
      circle.setAttribute('cx', String(x));
      circle.setAttribute('cy', String(y));
      circle.setAttribute('r', String(NODE_R));
      circle.setAttribute('fill', COLORS.nodeFill);
      circle.setAttribute('stroke', borderColor);
      circle.setAttribute('stroke-width', '2');
      g.appendChild(circle);

      // Title label — placed outboard of node, anchored toward the axis
      const title = item.title || item.slug;
      const displayTitle = title.length > 24 ? title.slice(0, 22) + '…' : title;
      const isRight = offset ? offset.right : true;
      const labelX = isRight ? x + NODE_R + 6 : x - NODE_R - 6;
      const anchor = isRight ? 'start' : 'end';

      const text = document.createElementNS(NS, 'text') as SVGTextElement;
      text.setAttribute('x', String(labelX));
      text.setAttribute('y', String(y));
      text.setAttribute('text-anchor', anchor);
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('fill', COLORS.text);
      text.setAttribute('font-family', "'JetBrains Mono', monospace");
      text.setAttribute('font-size', '11');
      text.setAttribute('pointer-events', 'none');
      text.textContent = displayTitle;
      g.appendChild(text);

      // Hover
      g.addEventListener('mouseenter', () => {
        circle.setAttribute('fill', COLORS.nodeHover);
        circle.setAttribute('stroke-width', '3');
        circle.setAttribute('r', String(NODE_R + 2));
      });
      g.addEventListener('mouseleave', () => {
        circle.setAttribute('fill', COLORS.nodeFill);
        circle.setAttribute('stroke-width', '2');
        circle.setAttribute('r', String(NODE_R));
      });
      g.addEventListener('click', () => onNodeClick(item.slug));

      nodeGroup.appendChild(g);
    }
    svg.appendChild(nodeGroup);
  }

  // Initial draw
  draw();

  // --- Interaction: zoom + pan ---
  let dragging = false;
  let dragStartY = 0;
  let centerYearStart = 0;

  svg.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault();

    // Ctrl+scroll (and trackpad pinch, which also sets ctrlKey) zooms.
    // Plain scroll pans along the time axis.
    if (e.ctrlKey) {
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      const newYPP = yearsPerPixel * factor;

      // Clamp zoom
      const minYPP = 0.001; // ~1 day per pixel
      const maxYPP = 50; // 50 years per pixel
      yearsPerPixel = Math.max(minYPP, Math.min(newYPP, maxYPP));

      // Zoom centered on mouse Y
      const cRect = container.getBoundingClientRect();
      const mouseY = e.clientY - cRect.top - cRect.height / 2;
      const yearAtMouse = centerYear + mouseY * (yearsPerPixel / factor);
      const newYearAtMouse = centerYear + mouseY * yearsPerPixel;
      centerYear += yearAtMouse - newYearAtMouse;
    } else {
      // Pan: deltaY scrolls forward in time (toward future / downward).
      const dy = e.deltaY + e.deltaX;
      centerYear += dy * yearsPerPixel;
    }

    draw();
  }, { passive: false });

  svg.addEventListener('mousedown', (e: MouseEvent) => {
    if ((e.target as Element).closest('.timeline-node') || (e.target as Element).closest('.timeline-arc')) return;
    dragging = true;
    dragStartY = e.clientY;
    centerYearStart = centerYear;
    svg.style.cursor = 'grabbing';
  });

  svg.addEventListener('mousemove', (e: MouseEvent) => {
    if (!dragging) return;
    const dy = e.clientY - dragStartY;
    centerYear = centerYearStart - dy * yearsPerPixel;
    draw();
  });

  function stopDrag(): void {
    dragging = false;
    svg.style.cursor = 'grab';
  }
  svg.addEventListener('mouseup', stopDrag);
  svg.addEventListener('mouseleave', stopDrag);
  svg.style.cursor = 'grab';

  // --- Resize observer ---
  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => draw());
    resizeObserver.observe(container);
  }

  return {
    setVisibleTypes(typeIds: number[] | null): void {
      if (typeIds === null) {
        visibleTypes = null;
      } else {
        visibleTypes = new Set<number | null>(typeIds);
      }
      draw();
    },
    destroy(): void {
      if (resizeObserver) resizeObserver.disconnect();
      container.innerHTML = '';
    },
  };
}
